import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { DepotProvider } from "./context/DepotContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Debtors from "./pages/Debtors";
import Payables from "./pages/Payables";
import PeriodReport from "./pages/PeriodReport";
import Sell from "./pages/Sell";
import SaleDoc from "./pages/SaleDoc";
import Quotations from "./pages/Quotations";
import QuotationDoc from "./pages/QuotationDoc";
import Trucks from "./pages/Trucks";
import Dashboard from "./pages/Dashboard";
import Products from "./pages/Products";
import StockMovementPage from "./pages/StockMovement";
import Transfers from "./pages/Transfers";
import Expenses from "./pages/Expenses";
import Cashflow from "./pages/Cashflow";
import DailyReport from "./pages/DailyReport";
import Users from "./pages/Users";
import Depots from "./pages/Depots";
import SignIns from "./pages/SignIns";

function Protected({ adminOnly, allow, children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-background" data-testid="auth-loading">
        <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  if (allow && !allow.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <DepotProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/" element={<Protected><Layout /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="products" element={<Products />} />
              <Route path="stock-in" element={<StockMovementPage key="in" type="stock_in" />} />
              <Route path="stock-out" element={<StockMovementPage key="out" type="stock_out" />} />
              <Route path="transfers" element={<Transfers />} />
              <Route path="expenses" element={<Expenses />} />
              <Route path="cashflow" element={<Cashflow />} />
              <Route path="report" element={<DailyReport />} />
              <Route path="debtors" element={<Debtors />} />
              <Route path="payables" element={<Payables />} />
              <Route path="sell" element={<Sell />} />
              <Route path="sales/:id" element={<SaleDoc />} />
              <Route path="quotations" element={<Quotations />} />
              <Route path="quotations/:id" element={<QuotationDoc />} />
              <Route path="trucks" element={<Protected allow={["admin", "truck_manager"]}><Trucks /></Protected>} />
              <Route path="period-report" element={<PeriodReport />} />
              <Route path="users" element={<Protected adminOnly><Users /></Protected>} />
              <Route path="depots" element={<Protected adminOnly><Depots /></Protected>} />
              <Route path="sign-ins" element={<Protected adminOnly><SignIns /></Protected>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors theme="dark" />
      </DepotProvider>
    </AuthProvider>
  );
}

export default App;
